'use client';

import { type ReactNode, useCallback, useMemo, useRef, useState } from 'react';
import { toast } from '@components/ui/sonner';
import { useImportQueueRail } from '@lib/import-queue-ui/context/import-queue-rail-context';
import { MAX_LISTING_IMPORT_FILES } from '@lib/listing-import/constants/listing-import-max-files';
import { listingQueryKeys } from '@lib/listing-view/hooks/listing-query-keys';
import { traceListingImportBatch } from '@lib/observability/listings/trace-listing-import-batch.client';
import { trackListingImportBatchStarted } from '@lib/observability/listings/track-listing-import-batch-started.client';
import { getLogger } from '@lib/observability/logging/log.client';
import { useQueryClient } from '@lib/query/client';
import { flushSync } from 'react-dom';
import { v4 as uuidv4 } from 'uuid';
import { useListingImportUpload } from '../hooks/use-listing-import-upload';
import type { ListingImportJob } from '../types/listing-import-job';
import type { ListingImportQueueItem } from '../types/listing-import-queue-item';
import { prepareListingImportFiles } from '../utils/prepare-listing-import-files';
import { ListingImportJobWaitTimeoutError, waitForListingImportJob } from '../utils/wait-for-listing-import-job';
import { ListingImportContext, type ListingImportContextValue } from './listing-import-context';
const {
  logError
} = getLogger('lib/listing-import/context/listing-import-provider');
const BETWEEN_IMPORTS_DELAY_MS = 300;
interface ListingImportProviderProps {
  children: ReactNode;
}
export function ListingImportProvider({
  children
}: ListingImportProviderProps) {
  const queryClient = useQueryClient();
  const {
    setOpen: setImportQueueRailOpen
  } = useImportQueueRail();
  const [queue, setQueue] = useState<ListingImportQueueItem[]>([]);
  const queueRef = useRef(queue);
  queueRef.current = queue;
  const uploadListingImport = useListingImportUpload();
  const runnerPromiseRef = useRef<Promise<void> | null>(null);
  const terminalToastImportIdsRef = useRef<Set<string>>(new Set());
  const batchSummaryShownRef = useRef<Set<string>>(new Set());
  const patchItem = useCallback((clientId: string, updates: Partial<Pick<ListingImportQueueItem, 'status' | 'importId' | 'errorMessage' | 'fileName'>>) => {
    const next = queueRef.current.map(item => item.clientId === clientId ? {
      ...item,
      ...updates
    } : item);
    queueRef.current = next;
    setQueue(next);
  }, []);
  const maybeShowBatchSummary = useCallback((batchId: string | undefined) => {
    if (!batchId || batchSummaryShownRef.current.has(batchId)) {
      return;
    }
    const batchItems = queueRef.current.filter(item_0 => item_0.batchId === batchId);
    const batchSize = batchItems[0]?.batchSize ?? 0;
    if (batchSize <= 1) {
      return;
    }
    const allTerminal = batchItems.every(item_1 => item_1.status === 'done' || item_1.status === 'failed');
    if (!allTerminal) {
      return;
    }
    batchSummaryShownRef.current.add(batchId);
    const successCount = batchItems.filter(item_2 => item_2.status === 'done').length;
    toast.success(`Imported ${successCount} of ${batchSize} files`);
  }, []);
  const handleTerminalJob = useCallback((item_3: ListingImportQueueItem, job: ListingImportJob) => {
    if (job.status === 'completed') {
      patchItem(item_3.clientId, {
        status: 'done',
        errorMessage: undefined
      });
      if (!terminalToastImportIdsRef.current.has(job.id)) {
        terminalToastImportIdsRef.current.add(job.id);
        queryClient.invalidateQueries({
          queryKey: listingQueryKeys.tree({}, item_3.orgId)
        });
        toast.success(`Imported ${job.createdCount} units` + (job.rejectedCount ? ` · ${job.rejectedCount} rejected` : ''));
      }
    } else if (job.status === 'failed') {
      patchItem(item_3.clientId, {
        status: 'failed',
        errorMessage: job.errorMessage ?? 'Listing import failed'
      });
      if (!terminalToastImportIdsRef.current.has(job.id)) {
        terminalToastImportIdsRef.current.add(job.id);
        toast.error(job.errorMessage ?? 'Listing import failed');
      }
    } else {
      return;
    }
    maybeShowBatchSummary(item_3.batchId);
  }, [maybeShowBatchSummary, patchItem, queryClient]);
  const executeImportItem = useCallback(async (item_4: ListingImportQueueItem) => {
    const importId = item_4.importId ?? uuidv4();
    patchItem(item_4.clientId, {
      status: 'uploading',
      importId
    });
    try {
      const {
        importId: returnedId
      } = await uploadListingImport.mutateAsync({
        orgId: item_4.orgId,
        file: item_4.file,
        importId
      });
      patchItem(item_4.clientId, {
        status: 'processing',
        importId: returnedId
      });
      const job_0 = await waitForListingImportJob(returnedId);
      if (job_0.status !== 'completed' && job_0.status !== 'failed') {
        throw new Error('Listing import did not reach a terminal status');
      }
      handleTerminalJob(item_4, job_0);
    } catch (err) {
      if (err instanceof ListingImportJobWaitTimeoutError) {
        logError('Listing import status polling timed out', err, {
          importId: err.importId
        });
        patchItem(item_4.clientId, {
          status: 'failed',
          errorMessage: 'Import status polling timed out'
        });
        toast.error('Import status polling timed out');
      } else {
        const message = err instanceof Error ? err.message : 'Import failed';
        patchItem(item_4.clientId, {
          status: 'failed',
          errorMessage: message
        });
        toast.error(message);
      }
      maybeShowBatchSummary(item_4.batchId);
    }
  }, [handleTerminalJob, maybeShowBatchSummary, patchItem, uploadListingImport]);
  const runSequentialImports = useCallback(async (fileCount: number) => {
    await traceListingImportBatch(fileCount, async () => {
      while (true) {
        const nextItem = queueRef.current.find(item_5 => item_5.status === 'queued');
        if (!nextItem) {
          break;
        }
        await executeImportItem(nextItem);
        const hasMoreQueued = queueRef.current.some(item_6 => item_6.status === 'queued');
        if (hasMoreQueued) {
          await new Promise(resolve => setTimeout(resolve, BETWEEN_IMPORTS_DELAY_MS));
        }
      }
    });
  }, [executeImportItem]);
  const ensureRunner = useCallback((fileCount_0: number) => {
    if (runnerPromiseRef.current) {
      return;
    }
    runnerPromiseRef.current = runSequentialImports(fileCount_0).finally(() => {
      runnerPromiseRef.current = null;
      const remainingQueued = queueRef.current.filter(item_7 => item_7.status === 'queued');
      if (remainingQueued.length > 0) {
        ensureRunner(remainingQueued.length);
      }
    });
  }, [runSequentialImports]);
  const startImport = useCallback(async (files: File[], orgId: string) => {
    if (files.length === 0) {
      return;
    }
    const {
      accepted,
      skippedNonCsvCount,
      capped
    } = prepareListingImportFiles(files);
    if (skippedNonCsvCount > 0) {
      toast.error(skippedNonCsvCount === 1 ? 'Skipped 1 file that is not a CSV' : `Skipped ${skippedNonCsvCount} files that are not CSVs`);
    }
    if (capped) {
      toast.error(`You can import up to ${MAX_LISTING_IMPORT_FILES} CSV files at a time`);
    }
    if (accepted.length === 0) {
      if (skippedNonCsvCount === 0 && !capped) {
        toast.error('Select at least one CSV file to import');
      }
      return;
    }
    trackListingImportBatchStarted({
      fileCount: accepted.length,
      skippedNonCsvCount,
      capped
    });
    const batchId_0 = uuidv4();
    const newItems: ListingImportQueueItem[] = accepted.map(file => ({
      clientId: uuidv4(),
      file,
      fileName: file.name,
      importId: uuidv4(),
      orgId,
      status: 'queued',
      batchId: batchId_0,
      batchSize: accepted.length
    }));
    flushSync(() => {
      const next_0 = [...queueRef.current, ...newItems];
      queueRef.current = next_0;
      setQueue(next_0);
    });
    setImportQueueRailOpen(true);
    ensureRunner(accepted.length);
  }, [ensureRunner, setImportQueueRailOpen]);
  const isUploading = useMemo(() => queue.some(item_8 => item_8.status === 'uploading'), [queue]);
  const value = useMemo<ListingImportContextValue>(() => ({
    queue,
    isUploading,
    startImport
  }), [isUploading, queue, startImport]);
  return <ListingImportContext.Provider value={value} data-sentry-element="ListingImportContext.Provider" data-sentry-component="ListingImportProvider" data-sentry-source-file="listing-import-provider.tsx">
      {children}
    </ListingImportContext.Provider>;
}